<script lang="ts">
    import { PRODUCT_VARIANT_DEFAULT_TITLE } from "@/constants";
    import { urlsBuilderContextToken } from "@/context_tokens";
    import { getTypedContext } from "@lib/svelte-typed-context";

    import Icon from "@components/Icon.svelte";
    import ArrowTopRightOnSquare from "@components/icons/ArrowTopRightOnSquare.svelte";
    import Image from "@components/Image/Image.svelte";
    import Money from "@components/Money.svelte";
    import ProductContainer from "@components/ProductContainer.svelte";

    export let productId: number;

    const urlsBuilder = getTypedContext(urlsBuilderContextToken);
</script>

<ProductContainer {productId} let:productInfo on:productReady>
    {@const product = productInfo.product}
    {@const productUrl = productInfo.productUrl}
    {#if product}
        <div class="gw-rv-product">
            <div class="gw-rv-product__inner">
                <div class="gw-rv-product__info">
                    <div class="gw-rv-product__left">
                        {#if product.featuredImage?.url || product.variants[0].image}
                            <Image
                                src={urlsBuilder.createProductImageUrl(
                                    product.featuredImage?.url ||
                                        product.variants[0].image?.url ||
                                        ""
                                )}
                                alt={product.featuredImage?.altText ||
                                    product.variants[0].image?.altText ||
                                    ""}
                                blur
                                imgClassName="gw-rv-product__image"
                                objectFit="cover"
                                on:imageLoaded
                            />
                        {/if}
                    </div>
                    <div class="gw-rv-product__right">
                        <a
                            class="gw-rv-product__link"
                            href={productUrl}
                            on:click|stopPropagation
                            title={product.title}
                            target="_blank"
                            aria-label={`Opens the product ${product.title} in a new tab`}
                        >
                            <h4 class="gw-rv-product__title">
                                {product.title}
                            </h4>
                            <div class="gw-rv-product__link-icon">
                                <Icon
                                    color="currentColor"
                                    width={12}
                                    height={12}
                                >
                                    <ArrowTopRightOnSquare />
                                </Icon>
                            </div>
                        </a>

                        {#if product.variants[0].title !== PRODUCT_VARIANT_DEFAULT_TITLE}
                            <h5 class="gw-rv-product__sub-title">
                                {product.variants[0].title}
                            </h5>
                        {/if}

                        {#if product.variants[0].price}
                            <span class="gw-rv-product__price">
                                <Money
                                    amount={product.variants[0].price.amount}
                                />
                            </span>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    {/if}
</ProductContainer>

<style lang="postcss">
    .gw-rv-product {
        background-color: inherit;

        &__inner {
            border-top: 1px solid #f0f0f0;
            padding: 1.25em 0 0;
            display: flex;
            justify-content: space-between;

            @media all and (max-width: 600px) {
                padding: 1em 0;
            }
        }

        &__info {
            width: 100%;
            display: flex;
            justify-content: space-between;
            gap: 1em;
            align-items: center;
        }

        &__left {
            max-width: 64px;
            min-width: 60px;
            height: 60px;
        }

        &__right {
            flex: 1;
        }

        &__link {
            text-decoration: none;
            color: inherit;
            display: flex;
            column-gap: 4px;
            align-items: center;

            &:hover {
                text-decoration: underline;
            }

            &-icon {
                visibility: hidden;
                line-height: 1;
                display: flex;
                align-items: center;
            }

            &:hover &-icon {
                visibility: visible;
            }
        }

        &__title {
            margin: 0;
            font-size: 0.875em;
            line-height: normal;
            font-weight: 600;
        }

        &__sub-title {
            margin: 2px 0 0;
            font-size: 0.75em;
            line-height: normal;
            font-weight: 400;
        }

        &__price {
            margin-top: 0.5em;
            display: inline-block;
            font-size: 0.75em;
        }
    }

    :global(.gw-rv-product__image) {
        border-radius: 3px;
        box-sizing: border-box;
        box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
    }
</style>
